import React, { Suspense, lazy } from 'react';
import { MapContainer, Marker, Popup } from 'react-leaflet';
import { IonPage, IonContent, IonSelect, IonSelectOption, IonSpinner, IonText, IonButton, IonIcon } from '@ionic/react';
import { createOutline, locationOutline, mapOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { componentsSummary } from '../../domain/Component';
import { connectivityOf } from '../../domain/connectivity';
import { useSiteMapPage } from '../../application/use-cases/sites/useSiteMapPage';
import BaseTileLayer from '../components/map/BaseTileLayer';
import MapAutoResize from '../components/map/MapAutoResize';
import { deviceMarkerIcon } from '../components/map/deviceMarkerIcon';
import '../components/map/leafletSetup';
import { FIELD_LAYER_CONFIG, formatFieldValue } from '../../domain/FieldMap';
import './SiteMap.css';

// three.js pesa: se baja recién cuando alguien abre el mapa 3D.
const Field3D = lazy(() => import('../components/map/Field3D'));

const DEFAULT_CENTER: [number, number] = [-34.6037, -58.3816];

const SiteMap: React.FC = () => {
  const history = useHistory();
  const {
    sites,
    selectedSiteId,
    setSelectedSiteId,
    selectedSite,
    siteDevices,
    devicesWithoutLocation,
    mode,
    setMode,
    availableLayers,
    activeLayer,
    setLayer,
    fieldStations,
    layerRange,
    bounds,
    loading,
    error,
    goToDevice,
  } = useSiteMapPage();

  const center: [number, number] =
    selectedSite?.lat !== null && selectedSite?.lat !== undefined && selectedSite?.lng !== null && selectedSite?.lng !== undefined
      ? [selectedSite.lat, selectedSite.lng]
      : DEFAULT_CENTER;

  const noLocationNote =
    devicesWithoutLocation > 0 ? `${devicesWithoutLocation} estación(es) de este campo todavía no tienen posición marcada.` : null;

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false} className="site-map-content">
        <div className="site-map-stage">
          {/* El mapa ocupa toda la pantalla; los controles flotan encima. */}
          {!loading && !error && sites.length > 0 && mode === '3d' && bounds && activeLayer && (
            <div className="site-map-canvas">
              <Suspense
                fallback={
                  <div className="site-map-center">
                    <IonSpinner color="light" />
                  </div>
                }
              >
                <Field3D bounds={bounds} layer={activeLayer} stations={fieldStations} onSelect={goToDevice} />
              </Suspense>
            </div>
          )}

          {!loading && !error && sites.length > 0 && mode === '2d' && (
            <div className="site-map-canvas is-2d">
              <MapContainer key={selectedSiteId ?? 'none'} center={center} zoom={16} style={{ height: '100%', width: '100%' }}>
                <BaseTileLayer />
                <MapAutoResize />
                {siteDevices.map((device) => (
                  <Marker key={device.id} position={[device.lat as number, device.lng as number]} icon={deviceMarkerIcon(device.status)}>
                    <Popup>
                      <strong>{device.name}</strong>
                      <br />
                      {connectivityOf(device).label}
                      <br />
                      {componentsSummary(device.components)}
                      <br />
                      <button
                        onClick={() => goToDevice(device.id)}
                        style={{ marginTop: 6, color: 'var(--ion-color-primary)', background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                      >
                        Ver estación
                      </button>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}

          <div className="site-map-top">
            <div className="site-map-bar">
              <h1>Mapa</h1>
              {sites.length > 0 && (
                <div className="site-map-mode" role="tablist" aria-label="Vista">
                  <button role="tab" aria-selected={mode === '3d'} onClick={() => setMode('3d')}>
                    3D
                  </button>
                  <button role="tab" aria-selected={mode === '2d'} onClick={() => setMode('2d')}>
                    2D
                  </button>
                </div>
              )}
            </div>

            {sites.length > 0 && (
              <div className="site-map-site">
                <IonIcon icon={locationOutline} />
                <IonSelect
                  interface="popover"
                  value={selectedSiteId}
                  placeholder="Elegí un campo"
                  onIonChange={(e) => setSelectedSiteId(e.detail.value)}
                  aria-label="Campo"
                >
                  {sites.map((site) => (
                    <IonSelectOption key={site.id} value={site.id}>
                      {site.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
                {selectedSiteId && (
                  <button className="site-map-edit" onClick={() => history.push('/app/sites')} aria-label="Editar campos">
                    <IonIcon icon={createOutline} />
                  </button>
                )}
              </div>
            )}

            {mode === '3d' && availableLayers.length > 0 && (
              <div className="site-map-layers" role="tablist" aria-label="Qué mostrar">
                {availableLayers.map((l) => (
                  <button key={l} role="tab" aria-selected={l === activeLayer} onClick={() => setLayer(l)}>
                    {FIELD_LAYER_CONFIG[l].label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {loading && (
            <div className="site-map-center">
              <IonSpinner color="primary" />
            </div>
          )}

          {!loading && error && (
            <div className="site-map-center">
              <div className="site-map-card">
                <IonText color="danger">
                  <p>{error}</p>
                </IonText>
              </div>
            </div>
          )}

          {!loading && !error && sites.length === 0 && (
            <div className="site-map-center">
              <div className="site-map-card">
                <IonIcon icon={mapOutline} className="site-map-card-icon" />
                <p>Todavía no creaste ningún campo. Creá uno y ubicá tus estaciones para verlas en el mapa.</p>
                <IonButton onClick={() => history.push('/app/sites')}>Crear un campo</IonButton>
              </div>
            </div>
          )}

          {!loading && !error && sites.length > 0 && mode === '3d' && !(bounds && activeLayer) && (
            <div className="site-map-center">
              <div className="site-map-card">
                <IonIcon icon={mapOutline} className="site-map-card-icon" />
                <p>
                  {bounds
                    ? 'Las estaciones de este campo todavía no mandaron datos.'
                    : 'Marcá la posición de las estaciones de este campo para verlas en el mapa.'}
                </p>
              </div>
            </div>
          )}

          {!loading && !error && sites.length > 0 && ((mode === '3d' && activeLayer && layerRange) || noLocationNote) && (
            <div className="site-map-bottom">
              {mode === '3d' && activeLayer && layerRange && (
                <>
                  <div className="site-map-legend">
                    <span>{formatFieldValue(activeLayer, layerRange[0])}</span>
                    <span
                      className="site-map-legend-bar"
                      style={{ background: `linear-gradient(to right, ${FIELD_LAYER_CONFIG[activeLayer].ramp.join(', ')})` }}
                    />
                    <span>{formatFieldValue(activeLayer, layerRange[1])}</span>
                  </div>
                  <p className="site-map-hint">
                    Más alto y más intenso = más {FIELD_LAYER_CONFIG[activeLayer].label.toLowerCase()}. Arrastrá para girar, pellizcá para
                    acercar y tocá una estación para abrirla.
                  </p>
                </>
              )}
              {noLocationNote && <p className="site-map-hint">{noLocationNote}</p>}
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SiteMap;
